import { useEffect, useRef, useState } from 'react';

// Hover/focus popover (a Popper with custom content) around a trigger, with keyboard
// hand-off between the trigger and the panel's primary action (ArrowDown) and focus
// restoration on dismissal. Used by the topic chips and the header credits.
export default function useHoverPanel(placement = 'top') {
  const control = useRef(null);
  const trigger = useRef(null);
  const panel = useRef(null);
  const primary = useRef(null);
  const suppressFocus = useRef(false);
  const hoverTimer = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const close = (restoreFocus = false) => {
    clearTimeout(hoverTimer.current);
    control.current?.close();
    if (restoreFocus) {
      suppressFocus.current = true;
      trigger.current?.focus({ preventScroll: true });
      suppressFocus.current = false;
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const dismissOutsideFocus = event => {
      if (!trigger.current?.contains(event.target) && !panel.current?.contains(event.target)) control.current?.close();
    };
    document.addEventListener('focusin', dismissOutsideFocus);
    return () => document.removeEventListener('focusin', dismissOutsideFocus);
  }, [open]);

  const openPanel = content => {
    clearTimeout(hoverTimer.current);
    if (suppressFocus.current) return;
    control.current?.open([], placement, trigger.current, content);
  };
  const hoverIn = show => { clearTimeout(hoverTimer.current); hoverTimer.current = setTimeout(show, 200); };
  const hoverOut = () => clearTimeout(hoverTimer.current);
  // The panel's links and buttons, in reading order.
  const focusables = () => [...(panel.current?.querySelectorAll('a[href], button:not([disabled])') || [])];
  const onPanelKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); close(true); }
    if (event.key === 'Tab' && event.shiftKey && document.activeElement === focusables()[0]) { event.preventDefault(); close(true); }
  };
  const onTriggerKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (!open) return;
    const target = event.key === 'ArrowDown' ? primary.current : event.key === 'Tab' && !event.shiftKey ? focusables()[0] : null;
    if (target && !target.disabled) { event.preventDefault(); target.focus(); }
  };

  return { control, trigger, panel, primary, open, setOpen, close, openPanel, hoverIn, hoverOut, onPanelKeyDown, onTriggerKeyDown };
}
